import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppHeader } from '@/components/layout/AppHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PolicyStatusBadge } from '@/components/policies/PolicyStatusBadge';
import { VersionHistoryTab } from '@/components/policies/VersionHistoryTab';
import { AcknowledgementsTab } from '@/components/policies/AcknowledgementsTab';
import { ReviewOwnershipTab } from '@/components/policies/ReviewOwnershipTab';
import { ReadOnlyBanner } from '@/components/policies/ReadOnlyBanner';
import { ApprovalConfirmationDialog } from '@/components/policies/ApprovalConfirmationDialog';
import { usePolicy, usePolicyVersions, usePolicyAcknowledgements } from '@/hooks/usePolicy';
import { useClinicStaff } from '@/hooks/useClinicStaff';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, History, Users, Settings, Archive, ArchiveRestore, Printer, ChevronRight, ChevronLeft, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { approvePolicy } from '@/hooks/useApprovePolicy';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { BackArrow } from '@/components/BackArrow';
import { useClinic } from '@/hooks/useClinic';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const printStyles = `
@media print {
  header, nav, .no-print, button, [class*="sidebar"] {
    display: none !important;
  }
  
  body {
    background: white;
  }
  
  .print-header {
    display: block !important;
    border-bottom: 2px solid black;
    padding-bottom: 12px;
    margin-bottom: 24px;
  }
}

@media screen {
  .print-header {
    display: none;
  }
}
`;

export default function PolicyDetail() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: userRole } = useUserRole();
  
  const [isApproving, setIsApproving] = useState(false);
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDownloadingPacket, setIsDownloadingPacket] = useState(false);

  // Tab scroll indicators
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    updateScrollIndicators();
    el.addEventListener('scroll', updateScrollIndicators, { passive: true });
    window.addEventListener('resize', updateScrollIndicators);
    return () => {
      el.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [updateScrollIndicators]);

  const { data: policy, isLoading: policyLoading, error: policyError, refetch } = usePolicy(id);
  const { data: versions = [], isLoading: versionsLoading } = usePolicyVersions(id);
  const { data: acknowledgements = [], isLoading: acksLoading } = usePolicyAcknowledgements(id);
  const { data: clinic } = useClinic();
  const { data: clinicStaff = [] } = useClinicStaff();

  // Compute pending acknowledgement count
  const latestVersion = versions[0] ?? null;
  const pendingAckCount = (() => {
    if (!latestVersion || policy?.status !== 'Approved') return 0;
    const ackedUserIds = new Set(
      acknowledgements
        .filter((a) => a.policy_version_id === latestVersion.id)
        .map((a) => a.acknowledged_by)
    );
    return clinicStaff.filter((s) => !ackedUserIds.has(s.id)).length;
  })();
  const { data: adminCount = 0 } = useQuery({
    queryKey: ['clinic-admin-count', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return 0;
      
      // Get all profiles in this clinic
      const { data: clinicProfiles } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('clinic_id', profile.clinic_id);
      
      if (!clinicProfiles || clinicProfiles.length === 0) return 0;
      
      // Get admin roles for these users
      const userIds = clinicProfiles.map(p => p.user_id);
      const { data: adminRoles } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'admin')
        .in('user_id', userIds);
      
      return adminRoles?.length ?? 0;
    },
    enabled: !!profile?.clinic_id && userRole === 'admin',
  });

  const isAdmin = userRole === 'admin';
  const isSelfApproval = policy?.created_by === profile?.id;
  const isOnlyAdmin = adminCount === 1;
  const isPolicyOwner = policy?.policy_owner === profile?.id;
  
  // Can approve if: admin AND (not self-approval OR is the only admin)
  const canApprove = isAdmin && (!isSelfApproval || isOnlyAdmin);
  
  // Is policy approved (read-only)
  const isApproved = policy?.status === 'Approved';
  
  // Can archive if: owner AND not already archived AND status is one of: draft, pending_approval, Approved
  const archivableStatuses = ['draft', 'pending_approval', 'Approved'];
  const canArchive = isPolicyOwner && !policy?.is_archived && archivableStatuses.includes(policy?.status || '');
  const canUnarchive = isPolicyOwner && policy?.is_archived;

  const handleSubmitForApproval = async () => {
    if (!policy) return;
    if (!latestVersion) return;
    setIsApproving(true);
    try {
      const { error } = await supabase
        .rpc('submit_policy_version_for_review', {
          p_policy_id: policy.id,
          p_version_id: latestVersion.id,
        });
      
      if (error) throw error;
      toast({ title: 'Policy submitted for approval' });
      refetch();

      // Trigger approval notification immediately (don't wait for cron)
      supabase.functions.invoke('trigger-approval-notification').catch(err =>
        console.error('Failed to trigger approval notifications:', err)
      );
    } catch (error) {
      toast({ title: 'Failed to submit for approval', variant: 'destructive' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleApproveClick = () => {
    setShowApprovalConfirmation(true);
  };

  const handleApproveConfirm = async () => {
    if (!policy || !profile) return;
    setIsApproving(true);
    try {
      await approvePolicy(policy.id, profile.id);
      toast({ title: 'Policy approved and locked' });
      setShowApprovalConfirmation(false);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['policy-versions', policy.id] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['pending-acknowledgements'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-stats'] });

      // Trigger acknowledgment notifications immediately on substantive approval
      // (don't wait for the hourly cron). Fire-and-forget — cron is the safety net.
      if (latestVersion?.is_substantive) {
        supabase.functions.invoke('trigger-acknowledgment-notification').catch(err =>
          console.error('Failed to trigger acknowledgment notifications:', err)
        );
      }
    } catch (error) {
      toast({ title: 'Failed to approve policy', variant: 'destructive' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleArchive = async () => {
    if (!policy || !profile || !archiveReason.trim()) return;
    setIsArchiving(true);
    try {
      const { error } = await supabase
        .from('policies')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
          archived_by: profile.id,
          archive_reason: archiveReason.trim(),
        })
        .eq('id', policy.id);
      
      if (error) throw error;
      toast({ title: 'Policy archived' });
      setArchiveDialogOpen(false);
      setArchiveReason('');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['compliance-stats'] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    } catch (error) {
      toast({ title: 'Failed to archive policy', variant: 'destructive' });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    if (!policy) return;
    setIsArchiving(true);
    try {
      const { error } = await supabase
        .from('policies')
        .update({
          is_archived: false,
          archived_at: null,
          archived_by: null,
          archive_reason: null,
        })
        .eq('id', policy.id);
      
      if (error) throw error;
      toast({ title: 'Policy unarchived' });
      refetch();
      queryClient.invalidateQueries({ queryKey: ['compliance-stats'] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
    } catch (error) {
      toast({ title: 'Failed to unarchive policy', variant: 'destructive' });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDownloadEvidencePacket = async () => {
    if (!latestVersion) return;
    setIsDownloadingPacket(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-evidence-packet', {
        body: { policy_version_id: latestVersion.id },
      });
      if (error) throw error;

      // data is already a Blob when Content-Type is application/pdf
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Evidence-Packet-${(policy?.name ?? 'Policy').replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Evidence Packet downloaded' });
    } catch (error) {
      console.error('Evidence packet download error:', error);
      toast({ title: 'Failed to generate Evidence Packet', variant: 'destructive' });
    } finally {
      setIsDownloadingPacket(false);
    }
  };

  // Get latest version number
  const latestVersionNumber = versions.length > 0 ? versions[0].version_number : undefined;

  return (
    <div className="min-h-screen bg-background">
      <style>{printStyles}</style>
      
      {/* Print Header - only visible when printing */}
      <div className="print-header">
        <p className="text-lg font-bold">{clinic?.name ?? 'Clinic'}</p>
        <p className="text-xl font-semibold">{policy?.name ?? 'Policy'}</p>
        <p className="text-sm text-muted-foreground mt-2">
          Printed on: {new Date().toLocaleDateString()}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Veracord — Compliance Record
        </p>
      </div>

      <AppHeader />
      
      <main className="container px-4 py-8">

        {policyLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-[300px]" />
            <Skeleton className="h-6 w-[150px]" />
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : policyError ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            Policy not found or you don't have access.
          </div>
        ) : policy ? (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <BackArrow to="/policies" />
                <FileText className="h-6 w-6 text-primary mr-3" />
                <div>
                  <h1 className="text-2xl md:text-[28px] font-extrabold tracking-tight leading-tight">{policy.name}</h1>
                  <p className="text-xs md:text-sm flex items-center gap-x-1.5 gap-y-0.5 flex-wrap">
                    <span className="font-bold">{policy.category}</span>
                    <span className="text-teal font-bold">|</span>
                    <span className="hidden md:inline text-muted-foreground">Current Policy Version:</span>
                    <span className="font-bold">v{latestVersion?.version_number ?? '—'}</span>
                    <span className="text-teal font-bold">|</span>
                    <span className="hidden md:inline text-muted-foreground">Last Updated:</span>
                    <span className="font-bold">{new Date(policy.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Evidence Packet button */}
                {isApproved && latestVersion && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadEvidencePacket}
                    disabled={isDownloadingPacket}
                    className="gap-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm transition-colors dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-500"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {isDownloadingPacket ? 'Generating...' : 'Evidence Packet'}
                  </Button>
                )}

                {/* Print button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.print()}
                  className="gap-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm transition-colors dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-500"
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print
                </Button>

                {/* Archive/Unarchive button - left of status badge */}
                {canArchive && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setArchiveDialogOpen(true)}
                    disabled={isArchiving}
                    className="gap-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm transition-colors dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-500"
                  >
                    <Archive className="h-3.5 w-3.5" />
                    Archive
                  </Button>
                )}
                {canUnarchive && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleUnarchive}
                    disabled={isArchiving}
                    className="gap-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm transition-colors dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:border-gray-500"
                  >
                    <ArchiveRestore className="h-3.5 w-3.5" />
                    {isArchiving ? 'Unarchiving...' : 'Unarchive'}
                  </Button>
                )}
                
                <PolicyStatusBadge status={policy.status} />
                {policy.status === 'draft' && isAdmin && (
                  <Button
                    size="sm"
                    onClick={handleSubmitForApproval}
                    disabled={isApproving}
                  >
                    {isApproving ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                )}
                {policy.status === 'pending_approval' && canApprove && (
                  <Button
                    size="sm"
                    onClick={handleApproveClick}
                    disabled={isApproving}
                  >
                    {isApproving ? 'Approving...' : 'Approve'}
                  </Button>
                )}
              </div>
            </div>

            {/* Read-only banner for approved policies */}
            {isApproved && !policy.is_archived && (
              <ReadOnlyBanner />
            )}

            {/* Tabs - Asana-style underline navigation */}
            <Tabs defaultValue="versions" className="w-full">
              <div className="relative">
                <TabsList ref={tabsRef} className="w-full justify-start gap-12 bg-transparent p-0 pr-8 h-auto rounded-none overflow-x-auto whitespace-nowrap" style={{ borderBottom: '1.5px solid hsl(var(--border))' }}>
                  <TabsTrigger 
                    value="versions" 
                    className="relative gap-2 px-1 py-3 rounded-none border-none shadow-none bg-transparent font-medium text-muted-foreground transition-all duration-200 data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-none hover:bg-muted/50 hover:text-foreground after:absolute after:-bottom-px after:left-0 after:right-0 after:h-[4px] after:rounded-sm after:bg-transparent data-[state=active]:after:bg-primary"
                  >
                    <History className="h-4 w-4" />
                    <span>Versions</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="acknowledgements" 
                    className="relative gap-2 px-1 py-3 rounded-none border-none shadow-none bg-transparent font-medium text-muted-foreground transition-all duration-200 data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-none hover:bg-muted/50 hover:text-foreground after:absolute after:-bottom-px after:left-0 after:right-0 after:h-[4px] after:rounded-sm after:bg-transparent data-[state=active]:after:bg-primary"
                  >
                    <Users className="h-4 w-4" />
                    <span>Acknowledgements</span>
                    {pendingAckCount > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">
                        {pendingAckCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="review" 
                    className="relative gap-2 px-1 py-3 rounded-none border-none shadow-none bg-transparent font-medium text-muted-foreground transition-all duration-200 data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-none hover:bg-muted/50 hover:text-foreground after:absolute after:-bottom-px after:left-0 after:right-0 after:h-[4px] after:rounded-sm after:bg-transparent data-[state=active]:after:bg-primary"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Details</span>
                  </TabsTrigger>
                </TabsList>
                {canScrollLeft && (
                  <div className="absolute left-0 top-0 bottom-0 w-8 pointer-events-none flex items-center justify-center md:hidden" style={{ background: 'linear-gradient(to left, transparent, #f2f2f2)' }}>
                    <ChevronLeft size={14} className="text-muted-foreground" />
                  </div>
                )}
                {canScrollRight && (
                  <div className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none flex items-center justify-center md:hidden" style={{ background: 'linear-gradient(to right, transparent, #f2f2f2)' }}>
                    <ChevronRight size={14} className="text-muted-foreground" />
                  </div>
                )}
              </div>

              <div className="mt-6">
                <TabsContent value="versions">
                  <VersionHistoryTab 
                    policyId={policy.id}
                    policyName={policy.name}
                    versions={versions} 
                    isLoading={versionsLoading}
                    policyStatus={policy.status}
                  />
                </TabsContent>

                <TabsContent value="acknowledgements">
                  <AcknowledgementsTab 
                    policyId={policy.id}
                    acknowledgements={acknowledgements} 
                    latestVersion={versions[0] ?? null}
                    versions={versions}
                    isLoading={acksLoading} 
                  />
                </TabsContent>

                <TabsContent value="review">
                  <ReviewOwnershipTab policy={policy} isLoading={policyLoading} latestVersion={versions[0] ?? null} />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        ) : null}
      </main>

      {/* Approval Confirmation Dialog */}
      <ApprovalConfirmationDialog
        open={showApprovalConfirmation}
        onOpenChange={setShowApprovalConfirmation}
        policyName={policy?.name ?? ''}
        versionNumber={latestVersionNumber}
        onConfirm={handleApproveConfirm}
        isLoading={isApproving}
      />

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive this policy?</DialogTitle>
            <DialogDescription>
              This will move the policy to the archive. You can unarchive it later if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="archive-reason">Reason for archiving</Label>
            <Input
              id="archive-reason"
              placeholder="e.g. Program cancelled, superseded by new policy"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setArchiveDialogOpen(false);
                setArchiveReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchive}
              disabled={isArchiving || !archiveReason.trim()}
            >
              {isArchiving ? 'Archiving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
